import { createSlice } from "@reduxjs/toolkit";

import {
  AutomationActionType,
  AutomationDTO,
  AutomationEntityTypeDTO,
  AutomationSettingsDTO,
  EntityFieldGroup,
  TriggerTypeDTO,
} from "../../models/Automation.model";
import {
  createAutomation,
  deleteAutomation,
  getAutomationActionTypes,
  getAutomationEntityTypes,
  getAutomationList,
  getAutomationSettings,
  getAutomationTriggerType,
  getEntityFields,
  setAutomationState,
} from "../thunks/automationThunks";

const emptyAutomationSettings: AutomationSettingsDTO = {
  automation_ID: 0,
  automation_Name: "",
  automation_Description: "",
  automation_IsEnabled: false,
  triggers: [],
};

export interface AutomationState {
  isAutomationListLoading: boolean;
  isTriggerTypesLoading: boolean;
  isEntityTypesLoading: boolean;
  isActionTypesLoading: boolean;
  isAutomationSettingsLoading: boolean;
  isEntityFieldsLoading: boolean;
  isCreateAutomationLoading: boolean;
  isDeleteAutomationLoading: boolean;
  isSetAutomationStateLoading: boolean;
  error: string | null;
  errorAction: string | null;
  isAutomationListInitialized: boolean;
  isTriggerTypesInitialized: boolean;
  isEntityTypesInitialized: boolean;
  isActionTypesInitialized: boolean;
  automations: AutomationDTO[];
  triggerTypeDTO: TriggerTypeDTO[];
  automationEntityTypeDTO: AutomationEntityTypeDTO[];
  automationSettings: AutomationSettingsDTO | null;
  entityFieldGroups: EntityFieldGroup[];
  actionTypes: AutomationActionType[];
}

const initialState: AutomationState = {
  automations: [],
  automationEntityTypeDTO: [],
  triggerTypeDTO: [],
  entityFieldGroups: [],
  actionTypes: [],
  isAutomationListLoading: false,
  isTriggerTypesLoading: false,
  isEntityTypesLoading: false,
  isActionTypesLoading: false,
  isAutomationSettingsLoading: false,
  isEntityFieldsLoading: false,
  isCreateAutomationLoading: false,
  isDeleteAutomationLoading: false,
  isSetAutomationStateLoading: false,
  error: null,
  errorAction: null,
  isAutomationListInitialized: false,
  isTriggerTypesInitialized: false,
  isEntityTypesInitialized: false,
  isActionTypesInitialized: false,
  automationSettings: emptyAutomationSettings,
};

const automationSlice = createSlice({
  name: "automation",
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    clearAutomationSettings: (state) => {
      state.automationSettings = emptyAutomationSettings;
      state.isAutomationSettingsLoading = false;
    },
    clearAutomationData: (state) => {
      state.automations = [];
      state.automationEntityTypeDTO = [];
      state.triggerTypeDTO = [];
      state.entityFieldGroups = [];
      state.isAutomationListInitialized = false;
      state.isTriggerTypesInitialized = false;
      state.isEntityTypesInitialized = false;
      state.isAutomationListLoading = false;
      state.isTriggerTypesLoading = false;
      state.isEntityTypesLoading = false;
      state.isAutomationSettingsLoading = false;
      state.isEntityFieldsLoading = false;
      state.isCreateAutomationLoading = false;
      state.isDeleteAutomationLoading = false;
      state.isSetAutomationStateLoading = false;
      state.automationSettings = emptyAutomationSettings;
      state.error = null;
      state.actionTypes = [];
      state.isActionTypesLoading = false;
      state.errorAction = null;
      state.isActionTypesInitialized = false;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getAutomationTriggerType.pending, (state) => {
        state.isTriggerTypesLoading = true;
        state.error = null;
      })
      .addCase(getAutomationTriggerType.fulfilled, (state, action) => {
        state.isTriggerTypesLoading = false;
        state.triggerTypeDTO = Array.isArray(action.payload.triggerTypes)
          ? action.payload.triggerTypes
          : [];
        state.isTriggerTypesInitialized = true;
      })
      .addCase(getAutomationTriggerType.rejected, (state, action) => {
        state.isTriggerTypesLoading = false;
        state.error = action.error.message || "Failed to fetch trigger types";
      })
      .addCase(getAutomationEntityTypes.pending, (state) => {
        state.isEntityTypesLoading = true;
        state.error = null;
      })
      .addCase(getAutomationEntityTypes.fulfilled, (state, action) => {
        state.isEntityTypesLoading = false;
        state.automationEntityTypeDTO = Array.isArray(
          action.payload.entityTypes,
        )
          ? action.payload.entityTypes
          : [];
        state.isEntityTypesInitialized = true;
      })
      .addCase(getAutomationEntityTypes.rejected, (state, action) => {
        state.isEntityTypesLoading = false;
        state.error = action.error.message || "Failed to fetch entity types";
      })
      .addCase(getAutomationList.pending, (state) => {
        state.isAutomationListLoading = true;
        state.error = null;
      })
      .addCase(getAutomationList.fulfilled, (state, action) => {
        state.isAutomationListLoading = false;
        state.automations = Array.isArray(action.payload.automations)
          ? action.payload.automations
          : [];
        state.isAutomationListInitialized = true;
      })
      .addCase(getAutomationList.rejected, (state, action) => {
        state.isAutomationListLoading = false;
        state.error = action.error.message || "Failed to fetch automation list";
      })
      .addCase(getAutomationSettings.pending, (state) => {
        state.isAutomationSettingsLoading = true;
        state.error = null;
        state.automationSettings = null;
      })
      .addCase(getAutomationSettings.fulfilled, (state, action) => {
        state.isAutomationSettingsLoading = false;
        state.automationSettings =
          action.payload.settings || emptyAutomationSettings;
      })
      .addCase(getAutomationSettings.rejected, (state, action) => {
        state.isAutomationSettingsLoading = false;
        state.error =
          action.error.message || "Failed to fetch automation settings";
      })
      .addCase(deleteAutomation.pending, (state) => {
        state.isDeleteAutomationLoading = true;
        state.error = null;
      })
      .addCase(deleteAutomation.fulfilled, (state, action) => {
        state.isDeleteAutomationLoading = false;
        const deletedID = action.meta.arg.automationID;
        state.automations = state.automations.filter(
          (a) => a.automation_ID !== deletedID,
        );
      })
      .addCase(deleteAutomation.rejected, (state, action) => {
        state.isDeleteAutomationLoading = false;
        state.error = action.error.message || "Failed to delete automation";
      })
      .addCase(getEntityFields.pending, (state) => {
        state.isEntityFieldsLoading = true;
        state.error = null;
      })
      .addCase(getEntityFields.fulfilled, (state, action) => {
        state.isEntityFieldsLoading = false;
        state.entityFieldGroups = Array.isArray(action.payload.fieldGroups)
          ? action.payload.fieldGroups
          : [];
      })
      .addCase(getEntityFields.rejected, (state, action) => {
        state.isEntityFieldsLoading = false;
        state.error = action.error.message || "Failed to fetch entity fields";
      })
      .addCase(setAutomationState.pending, (state) => {
        state.isSetAutomationStateLoading = true;
        state.error = null;
      })
      .addCase(setAutomationState.fulfilled, (state, action) => {
        state.isSetAutomationStateLoading = false;
        const { automationID, isEnabled } = action.meta.arg;
        const found = state.automations.find(
          (a) => a.automation_ID === automationID,
        );
        if (found) found.automation_IsEnabled = isEnabled;
        if (state.automationSettings?.automation_ID === automationID) {
          state.automationSettings.automation_IsEnabled = isEnabled;
        }
      })
      .addCase(setAutomationState.rejected, (state, action) => {
        state.isSetAutomationStateLoading = false;
        state.error = action.error.message || "Failed to set automation state";
      })
      .addCase(createAutomation.pending, (state) => {
        state.isCreateAutomationLoading = true;
        state.error = null;
      })
      .addCase(createAutomation.fulfilled, (state) => {
        state.isCreateAutomationLoading = false;
      })
      .addCase(createAutomation.rejected, (state, action) => {
        state.isCreateAutomationLoading = false;
        state.error = action.error.message || "Failed to create automation";
      })
      .addCase(getAutomationActionTypes.pending, (state) => {
        state.isActionTypesLoading = true;
        state.errorAction = null;
      })
      .addCase(getAutomationActionTypes.fulfilled, (state, action) => {
        state.isActionTypesLoading = false;
        state.actionTypes = Array.isArray(action.payload.actionTypes)
          ? action.payload.actionTypes
          : [];
        state.isActionTypesInitialized = true;
      })
      .addCase(getAutomationActionTypes.rejected, (state, action) => {
        state.isActionTypesLoading = false;
        // Avoid an endless refetch loop when the endpoint keeps failing (e.g. 500).
        state.isActionTypesInitialized = true;
        state.errorAction =
          action.error.message || "Failed to fetch automation action types";
      });
  },
});

export const { clearError, clearAutomationSettings, clearAutomationData } =
  automationSlice.actions;
export default automationSlice.reducer;
